import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { get, post } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { TableauDetail } from "@/components/analyse/TableauDetail";
import { LoadingState } from "@/components/common/States";

const inputCls = "h-8 bg-input border border-border rounded-sm px-2 text-xs text-foreground w-full";
const Field = ({ label, children, required }) => (
  <label className="block text-[11px] font-mono text-muted-foreground">
    {label}{required && <span className="text-destructive"> *</span>}
    <div className="mt-1">{children}</div>
  </label>
);

/** AUDIT INV V2 §8 et §6.3 — Vue Transferts dédiée (W6).
 *  Transfert ATOMIQUE : backend crée les deux mouvements (-X source, +X destination) en une
 *  transaction. Si l'une échoue, aucun stock n'est créé/perdu. UI construit automatiquement
 *  la hiérarchie Entrepôt → Zone → Emplacement pour source et destination. */
export const TransfertsPanel = ({ produits, emplacements, lots }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ produit_id: "", quantite: "", unite: "", lot_id: "", motif: "",
    emplacement_source_id: "", emplacement_dest_id: "", commentaire: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const historique = useQuery({ queryKey: ["inv-transferts"],
    queryFn: () => get("/inventaire/mouvements", { page: 1, page_size: 20, type: "transfert" }),
    refetchInterval: 60000 });

  const faire = useMutation({
    mutationFn: () => post("/inventaire/mouvements", { ...f, type: "transfert",
      produit_id: Number(f.produit_id), quantite: Number(f.quantite),
      lot_id: f.lot_id ? Number(f.lot_id) : null,
      emplacement_source_id: Number(f.emplacement_source_id),
      emplacement_dest_id: Number(f.emplacement_dest_id) }),
    onSuccess: () => {
      toast.success("Transfert enregistré (deux mouvements atomiques créés)");
      setF({ produit_id: "", quantite: "", unite: "", lot_id: "", motif: "",
        emplacement_source_id: "", emplacement_dest_id: "", commentaire: "" });
      qc.invalidateQueries({ queryKey: ["inv-transferts"] });
      qc.invalidateQueries({ queryKey: ["inv-stock"] });
      qc.invalidateQueries({ queryKey: ["inv-mvts"] });
      qc.invalidateQueries({ queryKey: ["inv-tableau-bord"] });
    },
    onError: (e) => toast.error(e.message),
  });

  // Filtrer les lots selon le produit sélectionné pour aider le magasinier.
  const lotsFiltres = f.produit_id ? (lots || []).filter((l) => String(l.produit_id) === String(f.produit_id)) : (lots || []);
  const sourceDiffDest = f.emplacement_source_id && f.emplacement_dest_id
    && f.emplacement_source_id !== f.emplacement_dest_id;
  const valid = f.produit_id && f.quantite && Number(f.quantite) > 0 && sourceDiffDest && f.motif;

  const emplOptions = (emplacements || []).filter((e) => e.actif).map((e) => (
    <option key={e.id} value={e.id}>{e.entrepot ? `${e.entrepot} / ` : ""}{e.zone ? `${e.zone} / ` : ""}{e.nom}</option>
  ));

  return (
    <div data-testid="transferts-panel" className="space-y-3">
      <Panel title="Nouveau transfert (atomique source → destination)" testId="panel-nouveau-transfert" bodyClassName="p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Field label="Produit" required>
            <select data-testid="transfert-produit" className={inputCls} value={f.produit_id} onChange={set("produit_id")}>
              <option value="">—</option>
              {(produits || []).map((p) => <option key={p.id} value={p.id}>{p.code} — {p.designation}</option>)}
            </select>
          </Field>
          <Field label="Lot (facultatif, traçabilité recommandée)">
            <select data-testid="transfert-lot" className={inputCls} value={f.lot_id} onChange={set("lot_id")}>
              <option value="">—</option>
              {lotsFiltres.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
            </select>
          </Field>
          <Field label="Quantité" required>
            <input data-testid="transfert-quantite" type="number" step="0.001" className={inputCls}
              value={f.quantite} onChange={set("quantite")} placeholder="ex. 1000" />
          </Field>
          <Field label="Motif" required>
            <select data-testid="transfert-motif" className={inputCls} value={f.motif} onChange={set("motif")}>
              <option value="">—</option>
              <option value="transfert">Transfert interne</option>
              <option value="correction">Correction d'emplacement</option>
              <option value="autre">Autre</option>
            </select>
          </Field>
          <Field label="Source (entrepôt / zone / emplacement)" required>
            <select data-testid="transfert-source" className={inputCls} value={f.emplacement_source_id} onChange={set("emplacement_source_id")}>
              <option value="">—</option>
              {emplOptions}
            </select>
          </Field>
          <Field label="Destination (entrepôt / zone / emplacement)" required>
            <select data-testid="transfert-dest" className={inputCls} value={f.emplacement_dest_id} onChange={set("emplacement_dest_id")}>
              <option value="">—</option>
              {emplOptions}
            </select>
          </Field>
          <Field label="Commentaire">
            <input data-testid="transfert-commentaire" className={inputCls} value={f.commentaire} onChange={set("commentaire")} />
          </Field>
          <div className="flex items-end">
            <button data-testid="transfert-submit" disabled={!valid || faire.isPending}
              className="h-8 px-4 w-full text-xs bg-primary text-primary-foreground rounded-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2"
              onClick={() => faire.mutate()}>
              <ArrowRightLeft className="h-3.5 w-3.5" />Transférer
            </button>
          </div>
        </div>
        {f.emplacement_source_id && f.emplacement_dest_id && f.emplacement_source_id === f.emplacement_dest_id && (
          <div className="mt-2 text-[11px] text-rose-400">Source et destination doivent être différentes.</div>
        )}
      </Panel>

      {historique.isLoading ? <LoadingState rows={5} /> : (
        <TableauDetail testId="table-historique-transferts" titre="Historique des transferts (20 derniers)"
          data={historique.data || { items: [], total: 0, pages: 1 }} page={1} setPage={() => {}}
          recherche="" setRecherche={() => {}}
          colonnes={[
            { cle: "horodatage", label: "Date / heure" },
            { cle: "produit", label: "Produit" },
            { cle: "lot", label: "Lot" },
            { cle: "quantite", label: "Quantité", align: "right",
              render: (m) => `${Math.abs(m.quantite) === m.quantite ? "+" : ""}${fmtNum(m.quantite)} ${m.unite || ""}` },
            { cle: "origine", label: "Source" },
            { cle: "destination", label: "Destination" },
            { cle: "utilisateur", label: "Utilisateur" },
            { cle: "motif", label: "Motif" },
          ]} />
      )}
    </div>
  );
};
